(() => {
  const CONFIG_SELECTOR = '[data-jrb-promo-entries-config]';
  const PROMO_PROPERTY = '_promo_entries_offer';
  const PROMO_COUNT_PROPERTY = '_promo_entries_count';
  const CAMPAIGN_ID_PROPERTY = '_promo_entries_campaign_id';
  const CAMPAIGN_HANDLE_PROPERTY = '_promo_entries_campaign_handle';
  const GIVEAWAY_PROPERTY = '_promo_entries_giveaway';
  const REDEMPTION_MODE_PROPERTY = '_promo_entries_redemption_mode';
  const EXPIRES_AT_PROPERTY = '_promo_entries_expires_at';
  const FREE_PROPERTY = '_free_entries_offer';
  const PROTECTION_PROPERTY = '_jrb_package_protection';
  const PROTECTION_SKU = 'JRB-PACKAGE-PROTECTION';
  const DEFAULT_HANDLE = 'promo-entries';
  const DEFAULT_SKU = 'PROMO-ENTRIES';

  class JRBPromoEntries {
    constructor(configElement) {
      this.configElement = configElement;
      this.config = this.readConfig(configElement);
      this.cart = null;
      this.busy = false;
      this.syncTimer = null;
      this.bound = false;
      this.observer = null;
      this.campaignTimer = null;
    }

    readConfig(element) {
      try {
        const parsed = JSON.parse(element?.textContent || '{}');
        parsed.variantId = Number(parsed.variantId || 0);
        parsed.customerTags = Array.isArray(parsed.customerTags) ? parsed.customerTags : [];
        parsed.campaigns = Array.isArray(parsed.campaigns) ? parsed.campaigns : [];
        return parsed;
      } catch (error) {
        console.error('[JRB PROMO-ENTRIES] Invalid storefront configuration.', error);
        return { variantId: 0, productHandle: DEFAULT_HANDLE, sku: DEFAULT_SKU, customerLoggedIn: false, customerTags: [], campaigns: [] };
      }
    }

    init() {
      if (this.bound) return;
      this.bound = true;
      document.addEventListener('cart:refresh', (event) => this.onCartEvent(event));
      document.addEventListener('cart:item-added', (event) => this.onCartEvent(event));
      document.addEventListener('line-item:change:end', (event) => this.onCartEvent(event));
      document.addEventListener('shopify:section:load', () => this.scheduleSync(null, true));
      document.addEventListener('click', (event) => this.onCheckoutClick(event), true);
      document.addEventListener('submit', (event) => this.onCheckoutSubmit(event), true);
      window.addEventListener('pageshow', () => this.scheduleSync(null, true));
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.scheduleSync(null, true); });
      this.observeCartMarkup();
      this.patchFreeEntriesController();
      [0, 100, 300, 1000].forEach((delay) => window.setTimeout(() => this.patchFreeEntriesController(), delay));
      this.scheduleCampaignBoundary();
      void this.sync(true);
    }

    getCartUrl() { return window.theme?.routes?.cart_url ? `${window.theme.routes.cart_url}.js` : '/cart.js'; }
    getAddUrl() { return window.theme?.routes?.cart_add_url ? `${window.theme.routes.cart_add_url}.js` : '/cart/add.js'; }
    getChangeUrl() { return window.theme?.routes?.cart_change_url || '/cart/change.js'; }
    normalizeBool(value) { return value === true || value === 'true'; }

    campaignIsCurrentlyValid(campaign, now = Date.now()) {
      if (!campaign || !this.normalizeBool(campaign.enabled)) return false;
      const campaignId = String(campaign.campaignId || '').trim();
      const handle = String(campaign.handle || '').trim();
      const entryCount = Number(campaign.entryCount || 0);
      const mode = String(campaign.redemptionMode || '').trim();
      const startsAt = Date.parse(campaign.startsAt || '');
      const expiresAt = Date.parse(campaign.expiresAt || '');
      if (!campaignId || !handle || entryCount <= 0) return false;
      if (!['one_time', 'unlimited'].includes(mode)) return false;
      if (!Number.isFinite(startsAt) || !Number.isFinite(expiresAt) || expiresAt <= startsAt) return false;
      return now >= startsAt && now < expiresAt;
    }

    eligibleCampaigns() {
      if (!this.config.customerLoggedIn) return [];
      const tags = new Set(this.config.customerTags.map((tag) => String(tag)));
      const now = Date.now();
      return this.config.campaigns.filter((campaign) => {
        if (!this.campaignIsCurrentlyValid(campaign, now)) return false;
        const id = String(campaign.campaignId);
        if (!tags.has(`PROMO-ENTRIES-ELIGIBLE-${id}`)) return false;
        if (String(campaign.redemptionMode) === 'one_time' && tags.has(`PROMO-ENTRIES-USED-${id}`)) return false;
        return true;
      }).sort((a, b) => {
        const diff = Number(b.priority || 0) - Number(a.priority || 0);
        return diff || String(a.campaignId || a.handle).localeCompare(String(b.campaignId || b.handle));
      });
    }

    selectedCampaign() { return this.eligibleCampaigns()[0] || null; }

    isPromoItem(item) {
      if (!item) return false;
      const props = item.properties || {};
      return Number(item.variant_id) === Number(this.config.variantId || 0) || item.sku === (this.config.sku || DEFAULT_SKU) || props[PROMO_PROPERTY] === 'true';
    }

    isFreeEntriesItem(item) { return item?.properties?.[FREE_PROPERTY] === 'true'; }
    isProtectionItem(item) { const props = item?.properties || {}; return props[PROTECTION_PROPERTY] === 'true' || item?.sku === PROTECTION_SKU; }

    qualifyingItems(cart = this.cart) {
      if (!cart || !Array.isArray(cart.items)) return [];
      return cart.items.filter((item) => Number(item.quantity || 0) > 0 && !this.isPromoItem(item) && !this.isFreeEntriesItem(item) && !this.isProtectionItem(item));
    }

    promoItems(cart = this.cart) {
      if (!cart || !Array.isArray(cart.items)) return [];
      return cart.items.filter((item) => this.isPromoItem(item));
    }

    expectedProperties(campaign) {
      return {
        [PROMO_PROPERTY]: 'true',
        [PROMO_COUNT_PROPERTY]: String(Number(campaign.entryCount)),
        [CAMPAIGN_ID_PROPERTY]: String(campaign.campaignId),
        [CAMPAIGN_HANDLE_PROPERTY]: String(campaign.handle),
        [GIVEAWAY_PROPERTY]: String(campaign.giveawayCode || ''),
        [REDEMPTION_MODE_PROPERTY]: String(campaign.redemptionMode),
        [EXPIRES_AT_PROPERTY]: String(campaign.expiresAt)
      };
    }

    itemMatchesCampaign(item, campaign) {
      if (!item || !campaign || Number(item.variant_id) !== Number(this.config.variantId) || Number(item.quantity || 0) !== 1) return false;
      const expected = this.expectedProperties(campaign);
      const props = item.properties || {};
      return Object.entries(expected).every(([key, value]) => String(props[key] ?? '') === value);
    }

    async fetchCart() {
      const response = await fetch(this.getCartUrl(), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to load cart for promotional entry verification.');
      return response.json();
    }

    async sync(forceFetch = false) {
      if (this.busy) return;
      this.busy = true;
      this.updateCheckoutState();
      this.patchFreeEntriesController();
      try {
        if (forceFetch || !this.cart) this.cart = await this.fetchCart();
        const campaign = this.selectedCampaign();
        const qualifying = this.qualifyingItems();
        const promoItems = this.promoItems();
        if (!campaign || !qualifying.length || !this.config.variantId) {
          if (promoItems.length) {
            await this.removePromoItems(promoItems);
            this.cart = await this.fetchCart();
            this.notifyRefresh();
          }
          return;
        }
        const matching = promoItems.filter((item) => this.itemMatchesCampaign(item, campaign));
        if (matching.length === 1 && promoItems.length === 1) return;
        if (promoItems.length) {
          await this.removePromoItems(promoItems);
          this.cart = await this.fetchCart();
        }
        await this.addPromoItem(campaign);
        this.cart = await this.fetchCart();
        this.notifyRefresh();
      } catch (error) {
        console.error('[JRB PROMO-ENTRIES]', error);
      } finally {
        this.busy = false;
        this.decoratePromoLines();
        this.updateCheckoutState();
        this.scheduleCampaignBoundary();
      }
    }

    scheduleSync(cart = null, forceFetch = false) {
      if (cart && Array.isArray(cart.items)) this.cart = cart;
      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => void this.sync(forceFetch || !cart), 100);
    }

    onCartEvent(event) {
      if (event?.detail?.source === 'jrb-promo-entries') return;
      const cart = event?.detail?.cart && Array.isArray(event.detail.cart.items) ? event.detail.cart : null;
      this.scheduleSync(cart, !cart);
    }

    async addPromoItem(campaign) {
      const response = await fetch(this.getAddUrl(), {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(this.config.variantId), quantity: 1, properties: this.expectedProperties(campaign) }] })
      });
      if (!response.ok) throw new Error('Unable to add promotional entries to cart.');
      await response.json();
    }

    async changeLine(id, quantity) {
      const response = await fetch(this.getChangeUrl(), {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id, quantity })
      });
      if (!response.ok) throw new Error('Unable to reconcile promotional entries in cart.');
      this.cart = await response.json();
      return this.cart;
    }

    async removePromoItems(items) { for (const item of items) await this.changeLine(item.key, 0); }

    patchFreeEntriesController() {
      const controller = window.jrbFreeEntriesOfferController;
      if (!controller || controller.__jrbPromoEntriesPatched || typeof controller.getQualifyingLineItems !== 'function') return;
      const original = controller.getQualifyingLineItems.bind(controller);
      controller.getQualifyingLineItems = (cart) => original(cart).filter((item) => !this.isPromoItem(item));
      controller.__jrbPromoEntriesPatched = true;
      if (typeof controller.scheduleSync === 'function') controller.scheduleSync(true);
    }

    decoratePromoLines() {
      const handle = this.config.productHandle || DEFAULT_HANDLE;
      const hrefPart = `/products/${handle}`;
      const campaign = this.selectedCampaign();
      const count = Number(campaign?.entryCount || this.promoItems()[0]?.properties?.[PROMO_COUNT_PROPERTY] || 0);
      document.querySelectorAll(`a[href*="${hrefPart}"]`).forEach((link) => {
        link.removeAttribute('href');
        link.removeAttribute('title');
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
        if (!link.querySelector('img, picture, svg')) link.textContent = count > 0 ? `${count} PROMO-ENTRIES` : 'PROMO-ENTRIES';
        const line = link.closest('tr') || link.closest('.product-cart-item');
        if (!line) return;
        line.classList.add('jrb-promo-entries-line');
        line.querySelectorAll('quantity-selector, cart-remove').forEach((control) => { control.hidden = true; control.style.display = 'none'; });
        const info = line.querySelector('.product-cart-item-info, .product-name > .product-cart-item > div:last-child');
        if (info && !info.querySelector('.jrb-promo-entries-line__note')) {
          const note = document.createElement('div');
          note.className = 'product-cart-item-options jrb-promo-entries-line__note';
          note.textContent = 'Promotional giveaway entries';
          info.appendChild(note);
        }
      });
    }

    observeCartMarkup() {
      if (!('MutationObserver' in window) || this.observer) return;
      this.observer = new MutationObserver(() => { this.decoratePromoLines(); this.patchFreeEntriesController(); });
      this.observer.observe(document.documentElement, { childList: true, subtree: true });
    }

    isCheckoutStateInvalid() {
      if (this.busy) return true;
      if (!this.cart) return false;
      const campaign = this.selectedCampaign();
      const qualifying = this.qualifyingItems();
      const promo = this.promoItems();
      if (!campaign || !qualifying.length) return promo.length > 0;
      return promo.length !== 1 || !this.itemMatchesCampaign(promo[0], campaign);
    }

    updateCheckoutState() {
      const invalid = this.isCheckoutStateInvalid();
      document.documentElement.classList.toggle('jrb-promo-entries-checkout-blocked', invalid);
      document.querySelectorAll('button[name="checkout"]').forEach((button) => {
        if (invalid) {
          button.dataset.jrbPromoEntriesDisabled = 'true';
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        } else if (button.dataset.jrbPromoEntriesDisabled === 'true') {
          delete button.dataset.jrbPromoEntriesDisabled;
          const freeBlocked = document.documentElement.classList.contains('jrb-free-entries-checkout-blocked');
          const protectionBlocked = button.dataset.jrbPackageProtectionDisabled === 'true';
          button.disabled = freeBlocked || protectionBlocked;
          button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
        }
      });
    }

    onCheckoutClick(event) {
      const button = event.target.closest?.('button[name="checkout"]');
      if (!button || !this.isCheckoutStateInvalid()) return;
      event.preventDefault(); event.stopPropagation(); this.scheduleSync(null, true);
    }

    onCheckoutSubmit(event) {
      if (!event.submitter?.matches?.('button[name="checkout"]') || !this.isCheckoutStateInvalid()) return;
      event.preventDefault(); event.stopPropagation(); this.scheduleSync(null, true);
    }

    scheduleCampaignBoundary() {
      clearTimeout(this.campaignTimer);
      if (!this.config.customerLoggedIn) return;
      const now = Date.now();
      const boundaries = [];
      for (const campaign of this.config.campaigns) {
        if (!this.normalizeBool(campaign.enabled)) continue;
        const start = Date.parse(campaign.startsAt || '');
        const end = Date.parse(campaign.expiresAt || '');
        if (Number.isFinite(start) && start > now) boundaries.push(start);
        if (Number.isFinite(end) && end > now) boundaries.push(end);
      }
      if (!boundaries.length) return;
      const next = Math.min(...boundaries);
      const delay = Math.max(Math.min(next - now + 250, 2147483000), 250);
      this.campaignTimer = setTimeout(() => this.scheduleSync(null, true), delay);
    }

    notifyRefresh() {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: this.cart, source: 'jrb-promo-entries' } }));
      [120, 350, 800].forEach((delay) => setTimeout(() => { this.decoratePromoLines(); this.patchFreeEntriesController(); }, delay));
    }
  }

  const mount = () => {
    const configElement = document.querySelector(CONFIG_SELECTOR);
    if (!configElement) return;
    if (!window.jrbPromoEntriesController) {
      window.jrbPromoEntriesController = new JRBPromoEntries(configElement);
      window.jrbPromoEntriesController.init();
    } else {
      window.jrbPromoEntriesController.configElement = configElement;
      window.jrbPromoEntriesController.config = window.jrbPromoEntriesController.readConfig(configElement);
      window.jrbPromoEntriesController.scheduleSync(null, true);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount); else mount();
  document.addEventListener('shopify:section:load', mount);
})();
